import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@immo/ui-kit';
import type { LandIqBoardColumn, LandIqBoardTicket, LandIqDataRunMetrics } from '@immo/api-contract';
import { ChevronDownIcon, ExternalLinkIcon, GripIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { CardRunMetrics } from '@/components/landiq/board/card-run-metrics';
import { ColumnStateTag } from '@/components/landiq/board/column-state-tag';
import { TicketEntryList } from '@/components/landiq/ticket-entry-list';

const STAGE_COLUMNS: ReadonlyArray<LandIqBoardColumn> = ['UPLOAD', 'BUILD', 'BACKFILL'];

type Props = {
  readonly ticket: LandIqBoardTicket;
  /** The latest run of the entry the card opens, when LandIQ has one. */
  readonly metrics: LandIqDataRunMetrics | null;
  readonly draggable: boolean;
  readonly dragging: boolean;
  readonly onDragStart: (ticket: LandIqBoardTicket) => void;
  readonly onDragEnd: () => void;
};

/**
 * One card, one keyboard stop: the title link's `::after` stretches over the card; the Linear link sits above it.
 * A dragged card stays mounted as the dashed placeholder, or the browser never fires `dragend` on it.
 */
export function BoardCard({ ticket, metrics, draggable, dragging, onDragStart, onDragEnd }: Props) {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const waiting = ticket.waitsFor.length > 0;
  const openEntry = ticket.entries.find((entry) => entry.id === ticket.openEntryId) ?? null;
  const openEntryRunning = openEntry?.state === 'RUNNING';

  const linearLink = (
    <a
      href={ticket.url}
      target="_blank"
      rel="noopener noreferrer"
      draggable={draggable ? false : undefined}
      aria-label={t('board.linear-label', { ticket: ticket.ticket })}
      title={t('board.linear')}
      className="relative z-10 inline-flex w-fit items-center gap-1 font-mono text-sm text-text-tertiary hover:text-text-primary"
    >
      {ticket.ticket}
      <ExternalLinkIcon aria-hidden className="h-3 w-3" />
    </a>
  );

  return (
    <li>
      <article
        draggable={draggable ? true : undefined}
        onDragStart={
          draggable
            ? (event) => {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', ticket.ticket);
                // After the browser has taken its drag image of the real card.
                setTimeout(() => onDragStart(ticket), 0);
              }
            : undefined
        }
        onDragEnd={draggable ? onDragEnd : undefined}
        className={cn(
          'relative flex min-w-0 flex-col gap-2 rounded-lg border border-border p-3 shadow-sm',
          ticket.column === 'PENDING' ? 'border-dashed' : 'border-solid',
          waiting ? 'bg-neutral-50' : 'bg-surface',
          draggable && 'cursor-grab',
          dragging && 'border-dashed border-neutral-300 bg-canvas shadow-none [&>*]:invisible',
        )}
      >
        {draggable ? (
          <div className="flex items-center justify-between">
            {linearLink}
            <GripIcon aria-hidden className="h-3.5 w-3.5 text-text-tertiary" />
          </div>
        ) : (
          linearLink
        )}
        <Link
          draggable={draggable ? false : undefined}
          to={`/landiq/data-runs/${encodeURIComponent(ticket.openEntryId)}`}
          title={ticket.title}
          className="block min-w-0 break-words text-sm font-medium text-text-primary line-clamp-3 after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-interactive"
        >
          {ticket.title}
        </Link>
        <StageTrail ticket={ticket} />
        {STAGE_COLUMNS.includes(ticket.column) && ticket.columnState !== null && (
          <div>
            <ColumnStateTag state={ticket.columnState} failureReason={ticket.failureReason} />
          </div>
        )}
        {waiting && (
          <p className="text-sm text-warning-700">{t('waits-for', { entries: ticket.waitsFor.join(', ') })}</p>
        )}
        <CardRunMetrics
          metrics={metrics}
          lastRunProgress={openEntry?.lastRunProgress ?? null}
          running={openEntryRunning}
          estimateMinutes={openEntry?.estimateMinutes ?? null}
          estimate={openEntry?.lastRunEstimate ?? null}
        />
        {ticket.entries.length > 1 && <EntriesDisclosure ticket={ticket} draggable={draggable} />}
      </article>
    </li>
  );
}

/**
 * A ticket with more than one entry (one per Stichtag, say) lists them all on demand, so the ones the title link
 * does not open can still be reached in one click. Failed entries lead.
 */
function EntriesDisclosure({ ticket, draggable }: { readonly ticket: LandIqBoardTicket; readonly draggable: boolean }) {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const listId = useId();
  const [open, setOpen] = useState(false);
  const failed = ticket.entries.filter((entry) => entry.state === 'FAILED').length;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        draggable={draggable ? false : undefined}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((value) => !value)}
        className="relative z-10 inline-flex w-fit items-center gap-1 rounded text-xs text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive"
      >
        <ChevronDownIcon aria-hidden className={cn('h-3 w-3 transition-transform', !open && '-rotate-90')} />
        {t('entries.toggle', { count: ticket.entries.length, failed })}
      </button>
      {open && (
        <TicketEntryList id={listId} ticket={ticket} draggable={draggable} className="border-t border-border pt-1" />
      )}
    </div>
  );
}

/**
 * The stages the manifest says this ticket needs, in column order, so the card says what the job is about and
 * where it can still go. The stage the card sits in reads stronger; a Pending or Imported card marks none.
 */
function StageTrail({ ticket }: { readonly ticket: LandIqBoardTicket }) {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const names = ticket.stages.map((stage) => t(`board.columns.${stage}`));

  return (
    <p
      className="flex flex-wrap items-center gap-1 text-xs text-text-tertiary"
      aria-label={t('board.stages-label', { stages: names.join(', ') })}
    >
      {ticket.stages.map((stage, index) => (
        <span key={stage} aria-hidden className="inline-flex items-center gap-1">
          {index > 0 && <span>→</span>}
          <span className={stage === ticket.column ? 'font-medium text-text-primary' : undefined}>{names[index]}</span>
        </span>
      ))}
    </p>
  );
}
